import React, { useState, useEffect, useCallback } from 'react';
import { MdCheckCircle, MdSearchOff, MdFilterList, MdOutlineSlowMotionVideo } from 'react-icons/md';
import { SiYoutubeshorts } from 'react-icons/si';
import { API_BASE_URL, normalizeMediaUrl } from './config';
import './SearchResultsPage.css';

function formatDuration(sec) {
  if (!sec && sec !== 0) return '0:00';
  const total = parseInt(sec, 10);
  if (isNaN(total)) return String(sec);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const padSec = seconds < 10 ? `0${seconds}` : `${seconds}`;
  if (hours > 0) {
    const padMin = minutes < 10 ? `0${minutes}` : `${minutes}`;
    return `${hours}:${padMin}:${padSec}`;
  }
  return `${minutes}:${padSec}`;
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return 'Recently';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'Recently';
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs} hour${diffHrs > 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  const diffYears = Math.floor(diffMonths / 12);
  return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
}

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, ' ').trim();
}

export default function SearchResultsPage({ searchQuery = '', onNavigate }) {
  const [results, setResults] = useState([]);
  const [shortsResults, setShortsResults] = useState([]);
  const [channelResults, setChannelResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All'); // 'All' | 'Videos' | 'Shorts' | 'Channels'

  // Extract clean query string from prop or window.location
  const currentQuery = (
    searchQuery ||
    new URLSearchParams(window.location.search).get('search_query') ||
    ''
  ).trim();

  // Synchronize browser tab title with search query
  useEffect(() => {
    if (currentQuery) {
      document.title = `${currentQuery} - SpeedoTube`;
    } else {
      document.title = 'SpeedoTube';
    }
  }, [currentQuery]);

  // One-time cleanup of any test videos created under 'aukatdikhadi'
  useEffect(() => {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.toLowerCase().includes('aukat')) {
          keysToRemove.push(k);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch (e) {}
  }, []);

  const fetchSearchResults = useCallback(async () => {
    if (!currentQuery) {
      setResults([]);
      setShortsResults([]);
      setChannelResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // 1. Fetch from SpeedoTube Backend Search API
      const res = await fetch(
        `${API_BASE_URL}/api/v1/search/videos?query=${encodeURIComponent(currentQuery)}&limit=30`,
        {
          headers: {
            token: 'c7e993808a344da9d59cfa20fa8b6061',
            devicetype: '0',
            request_type: 'USER',
          },
        }
      );

      let backendVideos = [];
      let backendChannels = [];
      if (res.ok) {
        const json = await res.json();
        const raw = json?.data?.videos || [];
        for (const v of raw) {
          if (v.type === 'channel' || (!v.video_url_id && !v.title)) {
            // It's a channel result - NOT a video!
            const chName = v.channel_name || v.name || 'Speedo Channel';
            if (chName.toLowerCase().includes('aukat')) continue;
            backendChannels.push({
              id: String(v.id),
              type: 'channel',
              channelName: chName,
              handleName: v.handle_name || '',
              channelAvatar: normalizeMediaUrl(v.image_url || v.avatar_url || v.avatar) || '',
              description: stripHtml(v.description) || '',
              subscribers: v.subscribers !== undefined ? Number(v.subscribers) : 0,
              verified: Boolean(Number(v.verified)),
              isUserSubscribed: Boolean(v.isUserSubscribed),
            });
          } else {
            // It's an actual video! Skip any dummy/untitled broken records
            if (!v.video_url_id) continue;
            const title = (v.title || '').trim();
            if ((!title || title === 'Untitled Video') && (!v.duration || v.duration === '0:00' || v.duration === 0)) {
              continue;
            }

            backendVideos.push({
              id: String(v.id),
              videoUrlId: v.video_url_id || String(v.id),
              title: title || 'Untitled Video',
              description: stripHtml(v.description) || 'Watch and enjoy on SpeedoTube.',
              duration: formatDuration(v.duration),
              channelName: v.channel_name || v.handle_name || 'Speedo Creator',
              handleName: v.handle_name || '',
              channelAvatar: v.channel_avatar || '',
              verified: v.verified !== undefined ? Boolean(Number(v.verified)) : true,
              views: v.views ? `${v.views} views` : (v.video_views ? `${v.video_views} views` : '0 views'),
              timestamp: formatRelativeTime(v.created_date),
              thumbnailUrl: normalizeMediaUrl(v.image_url || v.thumbnail_url) || (process.env.PUBLIC_URL || '') + '/img/banner.png',
              isShort: Boolean(v.is_short === 1 || v.isShort),
            });
          }
        }
      }

      // 2. Also check locally saved creator uploads in localStorage
      let localMatches = [];
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('speedotube_channel_videos_')) {
            if (key.toLowerCase().includes('aukat')) {
              localStorage.removeItem(key);
              continue;
            }
            const rawItems = JSON.parse(localStorage.getItem(key) || '[]');
            if (Array.isArray(rawItems)) {
              // Filter out any testing videos from aukatdikhadi
              const filteredRaw = rawItems.filter((item) => {
                const ch = (item.channelName || item.channelHandle || item.handleName || '').toLowerCase();
                return !ch.includes('aukat');
              });
              if (filteredRaw.length !== rawItems.length) {
                localStorage.setItem(key, JSON.stringify(filteredRaw));
              }

              const matched = filteredRaw.filter((item) => {
                const q = currentQuery.toLowerCase();
                const titleMatch = (item.title || '').toLowerCase().includes(q);
                const descMatch = (item.description || '').toLowerCase().includes(q);
                const tagMatch = (item.tags || '').toLowerCase().includes(q);
                return titleMatch || descMatch || tagMatch;
              }).map((item) => ({
                id: String(item.id || item.dbId || Math.random()),
                videoUrlId: item.videoUrlId || item.id,
                title: item.title,
                description: stripHtml(item.description) || 'Uploaded via Speedo Studio',
                duration: typeof item.duration === 'string' ? item.duration : formatDuration(item.duration),
                channelName: item.channelName || (item.channelHandle ? item.channelHandle.replace('@', '') : 'My Channel'),
                handleName: item.channelHandle || '',
                channelAvatar: '',
                verified: true,
                views: `${item.views || 0} views`,
                timestamp: formatRelativeTime(item.createdAt),
                thumbnailUrl: normalizeMediaUrl(item.thumbnailUrl) || (process.env.PUBLIC_URL || '') + '/img/banner.png',
                isShort: Boolean(item.isShort),
              }));
              localMatches = [...localMatches, ...matched];
            }
          }
        }
      } catch (e) {
        console.warn('Local search lookup error:', e);
      }

      // Merge and deduplicate (excluding any aukat test videos)
      const seen = new Set();
      const combined = [];
      for (const item of [...backendVideos, ...localMatches]) {
        const ch = (item.channelName || item.handleName || item.channelHandle || '').toLowerCase();
        if (ch.includes('aukat')) continue;

        // Skip any dummy or untitled videos that have no valid videoUrlId or 0:00 duration
        if (!item.videoUrlId) continue;
        if (item.title === 'Untitled Video' && (!item.duration || item.duration === '0:00')) continue;

        const uniqueKey = item.videoUrlId || item.id;
        if (!seen.has(uniqueKey)) {
          seen.add(uniqueKey);
          combined.push(item);
        }
      }

      // Filter channels (excluding aukat)
      const filteredChannels = backendChannels.filter((ch) => {
        const name = (ch.channelName || ch.handleName || '').toLowerCase();
        return !name.includes('aukat');
      });

      // Separate into main videos and Shorts
      const vids = combined.filter((item) => !item.isShort);
      const shts = combined.filter((item) => item.isShort);

      setResults(vids);
      setShortsResults(shts);
      setChannelResults(filteredChannels);
    } catch (err) {
      console.warn('Search query error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentQuery]);

  useEffect(() => {
    fetchSearchResults();
  }, [fetchSearchResults]);

  const handleVideoClick = (video) => {
    const isShort = Boolean(video.isShort);
    const watchPath = isShort
      ? `/shorts/${video.videoUrlId || video.id}`
      : `/watch?v=${video.videoUrlId || video.id}`;
    if (onNavigate) {
      onNavigate(watchPath);
    } else {
      window.history.pushState({}, '', watchPath);
      window.dispatchEvent(
        new CustomEvent('speedotube_navigate', {
          detail: { path: watchPath, videoId: video.videoUrlId || video.id },
        })
      );
    }
  };

  const handleChannelClick = (e, handleName) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    if (!handleName) return;
    const cleanHandle = handleName.startsWith('@') ? handleName : `@${handleName}`;
    const channelPath = `/channel/${encodeURIComponent(cleanHandle)}`;
    if (onNavigate) {
      onNavigate(channelPath);
    } else {
      window.history.pushState({}, '', channelPath);
      window.dispatchEvent(
        new CustomEvent('speedotube_navigate', {
          detail: { path: channelPath },
        })
      );
    }
  };

  const totalResultsCount =
    (activeFilter === 'Shorts' || activeFilter === 'Channels' ? 0 : results.length) +
    (activeFilter === 'Videos' || activeFilter === 'Channels' ? 0 : shortsResults.length) +
    (activeFilter === 'Videos' || activeFilter === 'Shorts' ? 0 : channelResults.length);

  return (
    <div className="yt-search-results-page">
      {/* Search Header / Filter Bar */}
      <div className="yt-search-header-container">
        <div className="yt-search-chips-row">
          <button
            type="button"
            className={`yt-search-chip ${activeFilter === 'All' ? 'active' : ''}`}
            onClick={() => setActiveFilter('All')}
          >
            All
          </button>
          <button
            type="button"
            className={`yt-search-chip ${activeFilter === 'Videos' ? 'active' : ''}`}
            onClick={() => setActiveFilter('Videos')}
          >
            Videos ({results.length})
          </button>
          <button
            type="button"
            className={`yt-search-chip ${activeFilter === 'Shorts' ? 'active' : ''}`}
            onClick={() => setActiveFilter('Shorts')}
          >
            Shorts ({shortsResults.length})
          </button>
          {channelResults.length > 0 && (
            <button
              type="button"
              className={`yt-search-chip ${activeFilter === 'Channels' ? 'active' : ''}`}
              onClick={() => setActiveFilter('Channels')}
            >
              Channels ({channelResults.length})
            </button>
          )}
        </div>
      </div>

      {/* Loading Skeleton */}
      {isLoading && (
        <div className="yt-search-skeleton-list">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="yt-search-skeleton-item">
              <div className="yt-search-skeleton-thumb" />
              <div className="yt-search-skeleton-info">
                <div className="yt-search-skeleton-line title" />
                <div className="yt-search-skeleton-line meta" />
                <div className="yt-search-skeleton-line channel" />
                <div className="yt-search-skeleton-line desc" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results List */}
      {!isLoading && (
        <>
          {/* Matching Channels (YouTube Channel Cards) */}
          {channelResults.length > 0 && (activeFilter === 'All' || activeFilter === 'Channels') && (
            <div className="yt-search-channels-container">
              {channelResults.map((channel) => (
                <div
                  key={channel.id}
                  className="yt-search-channel-card"
                  onClick={(e) => handleChannelClick(e, channel.handleName || channel.channelName)}
                  title={`View channel ${channel.channelName}`}
                >
                  <div className="yt-search-channel-card-avatar-box">
                    {channel.channelAvatar ? (
                      <img
                        src={channel.channelAvatar}
                        alt={channel.channelName}
                        className="yt-search-channel-card-avatar"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : null}
                    <div className="yt-search-channel-card-avatar-fallback">
                      {(channel.channelName || 'S').charAt(0).toUpperCase()}
                    </div>
                  </div>

                  <div className="yt-search-channel-card-info">
                    <div className="yt-search-channel-card-title-row">
                      <h3 className="yt-search-channel-card-name">{channel.channelName}</h3>
                      {channel.verified && (
                        <MdCheckCircle size={16} className="yt-search-verified-icon" title="Verified Channel" />
                      )}
                    </div>
                    <div className="yt-search-channel-card-meta">
                      {channel.handleName && <span>{channel.handleName}</span>}
                      {channel.handleName && <span className="yt-search-dot">•</span>}
                      <span>{channel.subscribers} subscriber{channel.subscribers === 1 ? '' : 's'}</span>
                    </div>
                    {channel.description && (
                      <p className="yt-search-channel-card-desc">{channel.description}</p>
                    )}
                  </div>

                  <div className="yt-search-channel-card-action">
                    <button
                      type="button"
                      className="yt-search-channel-visit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChannelClick(e, channel.handleName || channel.channelName);
                      }}
                    >
                      Visit Channel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Matching Shorts Shelf (when filter is 'All' or 'Shorts') */}
          {shortsResults.length > 0 && activeFilter !== 'Videos' && activeFilter !== 'Channels' && (
            <div className="yt-search-shorts-shelf-container">
              <div className="yt-search-shorts-header">
                <div className="yt-search-shorts-title-group">
                  <span className="yt-search-shorts-icon">
                    <SiYoutubeshorts size={22} color="#ff0000" />
                  </span>
                  <h3 className="yt-search-shorts-title">Shorts matching "{currentQuery}"</h3>
                </div>
              </div>
              <div className="yt-search-shorts-grid">
                {shortsResults.map((short) => (
                  <div
                    key={short.id}
                    className="yt-search-short-card"
                    onClick={() => handleVideoClick(short)}
                    title={short.title}
                  >
                    <div className="yt-search-short-thumb-wrap">
                      <img
                        src={short.thumbnailUrl}
                        alt={short.title}
                        className="yt-search-short-thumb-img"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = (process.env.PUBLIC_URL || '') + '/img/banner.png';
                        }}
                      />
                      <div className="yt-search-short-overlay">
                        <h4 className="yt-search-short-card-title">{short.title}</h4>
                        <span className="yt-search-short-card-views">{short.views}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Regular Videos List (Horizontal YouTube Cards) */}
          {activeFilter !== 'Shorts' && activeFilter !== 'Channels' && results.length > 0 && (
            <div className="yt-search-video-list">
              {results.map((video) => (
                <div
                  key={video.id}
                  className="yt-search-video-row"
                  onClick={() => handleVideoClick(video)}
                  title={video.title}
                >
                  {/* Left: Thumbnail with Duration Badge */}
                  <div className="yt-search-thumb-box">
                    <img
                      src={video.thumbnailUrl}
                      alt={video.title}
                      className="yt-search-thumb-img"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = (process.env.PUBLIC_URL || '') + '/img/banner.png';
                      }}
                    />
                    {video.duration && (
                      <span className="yt-search-duration-badge">{video.duration}</span>
                    )}
                  </div>

                  {/* Right: Metadata & Details */}
                  <div className="yt-search-content-box">
                    <h3 className="yt-search-item-title">{video.title}</h3>
                    <div className="yt-search-item-meta">
                      <span>{video.views}</span>
                      <span className="yt-search-dot">•</span>
                      <span>{video.timestamp}</span>
                    </div>

                    <div
                      className="yt-search-channel-line"
                      onClick={(e) => handleChannelClick(e, video.handleName || video.channelName)}
                      title={`Go to ${video.channelName}`}
                    >
                      <div className="yt-search-channel-avatar">
                        {video.channelAvatar ? (
                          <img src={video.channelAvatar} alt={video.channelName} />
                        ) : (
                          <span>{(video.channelName || 'S').charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <span className="yt-search-channel-name">{video.channelName}</span>
                      {video.verified && (
                        <MdCheckCircle size={14} className="yt-search-verified-icon" title="Verified" />
                      )}
                    </div>

                    <p className="yt-search-item-desc">{video.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State when no results found */}
          {totalResultsCount === 0 && (
            <div className="yt-search-empty-container">
              <div className="yt-search-empty-icon-wrap">
                <MdSearchOff size={68} />
              </div>
              <h2 className="yt-search-empty-title">
                No results found for "{currentQuery}"
              </h2>
              <p className="yt-search-empty-subtitle">
                Try different keywords, search for a channel name, or check for spelling errors.
              </p>
              <button
                type="button"
                className="yt-search-empty-btn"
                onClick={() => {
                  if (onNavigate) onNavigate('/');
                  else {
                    window.history.pushState({}, '', '/');
                    window.dispatchEvent(new CustomEvent('speedotube_navigate', { detail: { path: '/' } }));
                  }
                }}
              >
                Explore Home Feed
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
